import React from 'react';

import type { InputBaseProps } from '@mui/material';
import { FormHelperText, InputBase } from '@mui/material';
import clsx from 'clsx';
import type { NumberFormatProps } from 'react-number-format';
import NumberFormat from 'react-number-format';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  error: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { disabled, error }) => ({
  fullWidth: {
    width: '100%',
  },
  input: {
    '&:focus-within': {
      border: error
        ? `1px solid ${theme.palette.error.main}`
        : `1px solid ${theme.appColors.primary}`,
    },
    border: error ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    display: 'flex',
    position: 'relative' as const,
  },
  inputBase: {
    color: disabled ? theme.palette.action.disabled : theme.appColors.iconsType,
    fontSize: 14,
    padding: theme.spacing(1),
  },
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
}));

interface StyleProps {
  disabled?: boolean;
}

interface AlineNumberInputProps
  extends NumberFormatProps, Pick<InputBaseProps, 'endAdornment' | 'startAdornment'> {
  dataCy?: string;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  hideHelperText?: boolean;
  inputBaseClassName?: string;
  inputClassName?: string;
  inputRef?: React.Ref<HTMLInputElement>;
}

export function AlineNumberInput(props: AlineNumberInputProps): JSX.Element {
  const {
    autoComplete = 'off',
    className,
    dataCy = 'aline-number-input',
    error,
    fullWidth = true,
    helperText = ' ',
    hideHelperText = false,
    inputBaseClassName,
    inputClassName,
    inputRef,
    ...rest
  } = props;

  const { classes } = useStyles({ disabled: rest.disabled, error: !!error });

  return (
    <div className={clsx(classes.inputWrapper, className, fullWidth && classes.fullWidth)}>
      <div className={clsx(classes.input, inputClassName)}>
        <NumberFormat
          autoComplete={autoComplete}
          className={clsx(inputBaseClassName, fullWidth && classes.fullWidth)}
          classes={{ input: classes.inputBase }}
          customInput={InputBase}
          data-cy={dataCy}
          // Additional inputRef prop is forwarded to InputBase by React number format.
          inputRef={inputRef}
          {...rest}
        />
      </div>
      {helperText && !hideHelperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </div>
  );
}
